import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './Dashboard.css';

const STATUS_LABELS = {
  pending_payment: { label: 'En attente de paiement', cls: 'badge-muted' },
  paid:            { label: 'Payée',                   cls: 'badge-success' },
  cancelled:       { label: 'Annulée',                 cls: 'badge-danger' },
  refunded:        { label: 'Remboursée',              cls: 'badge-muted' },
};

const PLAN_LABELS = { monthly: 'Mensuel', annual: 'Annuel', free: 'Gratuit' };

export default function Dashboard() {
  const { client } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/modules/orders')
      .then(res => setOrders(res.data.orders || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const activeOrders = orders.filter(o => o.status === 'paid' && o.licence_key);
  const pendingOrders = orders.filter(o => o.status === 'pending_payment');

  return (
    <div className="dashboard-page">
      <Navbar />

      <div className="dashboard-hero">
        <div className="container">
          <h1>Bonjour, {client?.first_name} 👋</h1>
          <p>Gérez vos licences et commandes EvBackupManager</p>
        </div>
      </div>

      <div className="container dashboard-content">

        {/* ── Licences actives ── */}
        <section className="dashboard-section">
          <div className="section-title">
            <h2>Licences actives</h2>
            <Link to="/order" className="btn btn-primary btn-sm">+ Nouvelle commande</Link>
          </div>

          {loading ? (
            <div className="dashboard-loading"><div className="spinner" style={{ border: '3px solid var(--border)', borderTopColor: 'var(--primary)' }} /></div>
          ) : activeOrders.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔑</div>
              <h3>Aucune licence active</h3>
              <p>Passez à la version premium pour débloquer Google Drive et le support 7j/7.</p>
              <Link to="/order" className="btn btn-primary">Commander une licence</Link>
            </div>
          ) : (
            <div className="licence-grid">
              {activeOrders.map(order => (
                <div key={order.id} className="licence-card">
                  <div className="licence-card-header">
                    <span className="product-name">🛡️ {order.product_name}</span>
                    <span className={`badge ${PLAN_LABELS[order.plan_type] === 'Annuel' ? 'badge-accent' : 'badge-primary'}`}>
                      {PLAN_LABELS[order.plan_type] || order.plan_type}
                    </span>
                  </div>
                  <div className="licence-key-block">
                    <label>Clé de licence</label>
                    <div className="licence-key">
                      <code>{order.licence_key}</code>
                      <button
                        className="copy-btn"
                        title="Copier"
                        onClick={() => navigator.clipboard.writeText(order.licence_key)}
                      >
                        📋
                      </button>
                    </div>
                  </div>
                  <div className="licence-meta">
                    <div className="meta-row">
                      <span>Domaine</span>
                      <strong>{order.domain || '—'}</strong>
                    </div>
                    <div className="meta-row">
                      <span>Expiration</span>
                      <strong>{order.expires_at ? new Date(order.expires_at).toLocaleDateString('fr-FR') : 'Illimité'}</strong>
                    </div>
                    <div className="meta-row">
                      <span>Commande #</span>
                      <strong>{order.id}</strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── Commandes en attente ── */}
        {pendingOrders.length > 0 && (
          <section className="dashboard-section">
            <div className="section-title"><h2>Commandes en attente</h2></div>
            <div className="alert alert-info">
              ⏳ Votre chèque n'a pas encore été encaissé. Dès réception, nous créerons votre licence et vous enverrons votre clé par email.
            </div>
            {pendingOrders.map(order => (
              <div key={order.id} className="order-row">
                <div className="order-info">
                  <strong>Commande #{order.id}</strong> — {order.product_name} · {PLAN_LABELS[order.plan_type]} · {order.amount} €
                </div>
                <div className="order-meta">
                  Domaine : <strong>{order.domain || '—'}</strong> ·
                  Passée le {new Date(order.created_at).toLocaleDateString('fr-FR')}
                </div>
                <div className="order-cheque">
                  📬 Envoyez votre chèque de <strong>{order.amount} €</strong> à l'ordre de <strong>Technonet</strong>.<br />
                  Inscrivez au dos : <code>{order.id} - {client?.email}</code>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* ── Historique ── */}
        <section className="dashboard-section">
          <div className="section-title"><h2>Historique des commandes</h2></div>
          {loading ? null : orders.length === 0 ? (
            <p className="text-muted">Aucune commande pour le moment.</p>
          ) : (
            <div className="orders-table-wrap">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Produit</th>
                    <th>Formule</th>
                    <th>Montant</th>
                    <th>Domaine</th>
                    <th>Statut</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => {
                    const s = STATUS_LABELS[order.status] || { label: order.status, cls: 'badge-muted' };
                    return (
                      <tr key={order.id}>
                        <td>#{order.id}</td>
                        <td>{order.product_name}</td>
                        <td>{PLAN_LABELS[order.plan_type] || order.plan_type}</td>
                        <td>{Number(order.amount).toFixed(2)} €</td>
                        <td>{order.domain || '—'}</td>
                        <td><span className={`badge ${s.cls}`}>{s.label}</span></td>
                        <td>{new Date(order.created_at).toLocaleDateString('fr-FR')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ── Téléchargement ── */}
        <section className="dashboard-section">
          <div className="section-title"><h2>Téléchargements</h2></div>
          <div className="download-card">
            <div className="download-info">
              <h3>🛡️ EvBackupManager — Version Gratuite</h3>
              <p>Backup local, FTP &amp; SFTP. Compatible PrestaShop 1.7 &amp; 8.x.</p>
            </div>
            <a href="/api/modules/download" className="btn btn-outline" download>
              ⬇️ Télécharger (.zip)
            </a>
          </div>
        </section>

      </div>

      <Footer />
    </div>
  );
}
